'use client';

import { VisuallyHidden } from '@frameio/vapor';
import {
  AssetPathnameContainer,
  Pathnames,
  PathnameSegmentText,
} from './AssetPathnameSegment';
import TEXT from './generated/TEXT/AssetPathnameDeleted';
import type { BasePathnameProps } from './types';

TEXT.DEFINE({
  /** Accessibility label for deleted asset location breadcrumb */
  ariaLabelDeleted: 'Deleted asset location',
  /** Label indicating the asset has been recently deleted */
  deleted: 'Recently Deleted',
});

const TEST_IDS_CONTAINER = 'asset-pathname-deleted';
const PATHNAME_SEPARATOR = '/';

/**
 * Shows pathname for a deleted asset in a search result.
 *
 * Displays the project name followed by "Recently Deleted" to indicate
 * the asset has been moved to the trash.
 *
 * ## Design Considerations:
 * - Uses consistent styling with other pathname components
 * - Maintains visual hierarchy with tertiary text color for project name
 * - Uses error color for deleted status to indicate the asset is no longer available
 * - Provides proper accessibility labels for screen readers
 */
export const AssetPathnameDeleted = ({ project }: BasePathnameProps) => {
  return (
    <AssetPathnameContainer
      aria-label={TEXT.ariaLabelDeleted}
      data-testid={TEST_IDS_CONTAINER}
    >
      <Pathnames>
        <PathnameSegmentText
          segments={2}
        >{`${project.name} /`}</PathnameSegmentText>
        <PathnameSegmentText color="error" segments={2}>
          {TEXT.deleted}
        </PathnameSegmentText>
      </Pathnames>
      <AssetPathnameDeletedScreenReader project={project} />
    </AssetPathnameContainer>
  );
};

/**
 * Creates accessible breadcrumb text for screen readers.
 * Provides the project name and deleted status for better context.
 */
function AssetPathnameDeletedScreenReader({ project }: BasePathnameProps) {
  const segments = [project.name, TEXT.deleted];
  const screenReaderContent = segments.join(` ${PATHNAME_SEPARATOR} `);
  return (
    <VisuallyHidden aria-live="polite">{screenReaderContent}</VisuallyHidden>
  );
}
